'use client';

/**
 * Motion primitives & Lenis smooth-scroll provider.
 *
 * - All Framer Motion variants live here so they stay consistent across pages.
 * - `useReducedMotion` is re-exported so components don't import from framer-motion directly.
 * - Lenis is initialised once at the root via <LenisProvider>; it auto-disables when
 *   the user prefers reduced motion.
 */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useReducedMotion as useFramerReducedMotion } from 'framer-motion';
import Lenis from 'lenis';

export const EASE_PREMIUM = [0.22, 1, 0.36, 1] as const;
export const EASE_PREMIUM_IN = [0.65, 0, 0.35, 1] as const;

export const DURATION = {
  micro: 0.2,
  fast: 0.3,
  base: 0.5,
  slow: 0.8,
  hero: 1.2,
} as const;

// ── Reusable variants ────────────────────────────────────────────────────
export const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_PREMIUM } },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: DURATION.fast, ease: EASE_PREMIUM } },
};

export const slideUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_PREMIUM } },
};

export const slideDown = {
  hidden: { opacity: 0, y: -20 },
  show: { opacity: 1, y: 0, transition: { duration: DURATION.fast, ease: EASE_PREMIUM } },
};

export const staggerContainer = (stagger = 0.1, delay = 0) => ({
  hidden: {},
  show: {
    transition: { staggerChildren: stagger, delayChildren: delay },
  },
});

// Per-word fade-up for hero headlines
export const wordFadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE_PREMIUM } },
};

// Hover lift used by cards
export const cardHover = {
  rest: { y: 0, transition: { duration: DURATION.fast, ease: EASE_PREMIUM } },
  hover: { y: -4, transition: { duration: DURATION.fast, ease: EASE_PREMIUM } },
};

// Page transition (matched by <PageTransition>)
export const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_PREMIUM } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: EASE_PREMIUM } },
};

// ── Hooks ────────────────────────────────────────────────────────────────
export function useReducedMotion(): boolean {
  return useFramerReducedMotion() ?? false;
}

// ── Lenis smooth-scroll provider ─────────────────────────────────────────
type LenisProviderProps = { children: ReactNode };

export function LenisProvider({ children }: LenisProviderProps) {
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      lerp: 0.1,
      duration: 1.1,
      smoothWheel: true,
    });

    const raf = (time: number) => {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    };
    rafRef.current = requestAnimationFrame(raf);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      lenis.destroy();
    };
  }, []);

  return <>{children}</>;
}

// ── Helpers ──────────────────────────────────────────────────────────────
/** Split a string into <span>-wrapped words; useful with `staggerContainer` + `wordFadeUp`. */
export function splitWords(text: string): string[] {
  return text.split(/(\s+)/);
}
